import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: ${color};
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

const pickupIcon = createIcon('#00BFA6');
const destinationIcon = createIcon('#FFD166');
const driverIcon = createIcon('#EF4444');

export const MapComponent = ({ 
  center = [14.6349, -90.5069], // Guatemala City default
  zoom = 13,
  pickupLocation,
  destinationLocation,
  driverLocation,
  onMapClick,
  className = "",
  showRoute = false,
  autoLocate = true, // Enable geolocation by default
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const routeLayerRef = useRef(null);
  const userLocationMarkerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map with light tile layer
    const map = L.map(mapRef.current, {
      center,
      zoom,
      zoomControl: false,
    });

    // CartoDB Positron tiles for light theme
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add zoom control on right
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    // Click handler - always register to ensure events work
    map.on('click', (e) => {
      console.log('[MapComponent] Map clicked at:', e.latlng.lat, e.latlng.lng);
      if (onMapClick) {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    });

    mapInstanceRef.current = map;

    // Try to get user's location
    if (autoLocate && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          // Center map on user location
          map.setView([userLat, userLng], 15);
          
          // Add user location marker
          if (userLocationMarkerRef.current) {
            map.removeLayer(userLocationMarkerRef.current);
          }
          
          const userIcon = L.divIcon({
            className: 'user-location-marker',
            html: `<div style="
              width: 16px;
              height: 16px;
              background: #4285F4;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(66,133,244,0.5);
            "></div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          });
          
          userLocationMarkerRef.current = L.marker([userLat, userLng], { icon: userIcon })
            .addTo(map)
            .bindPopup('Tu ubicación');
        },
        (error) => {
          console.log('Geolocation not available:', error.message);
          // Keep default Guatemala center
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old markers
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};

    // Add pickup marker
    if (pickupLocation) {
      const marker = L.marker([pickupLocation.lat, pickupLocation.lng], { icon: pickupIcon })
        .addTo(map)
        .bindPopup('Punto de recogida');
      markersRef.current.pickup = marker;
    }

    // Add destination marker
    if (destinationLocation) {
      const marker = L.marker([destinationLocation.lat, destinationLocation.lng], { icon: destinationIcon })
        .addTo(map)
        .bindPopup('Destino');
      markersRef.current.destination = marker;
    }

    // Add driver marker
    if (driverLocation) {
      const marker = L.marker([driverLocation.lat, driverLocation.lng], { icon: driverIcon })
        .addTo(map)
        .bindPopup('Conductor');
      markersRef.current.driver = marker;
    }

    // Fit bounds if we have both pickup and destination
    if (pickupLocation && destinationLocation) {
      const bounds = L.latLngBounds([
        [pickupLocation.lat, pickupLocation.lng],
        [destinationLocation.lat, destinationLocation.lng]
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (pickupLocation) {
      map.setView([pickupLocation.lat, pickupLocation.lng], 15);
    }

    // Draw route line
    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
    }

    if (showRoute && pickupLocation && destinationLocation) {
      const routeLine = L.polyline([
        [pickupLocation.lat, pickupLocation.lng],
        [destinationLocation.lat, destinationLocation.lng]
      ], {
        color: '#00BFA6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 10',
      }).addTo(map);
      routeLayerRef.current = routeLine;
    }

  }, [pickupLocation, destinationLocation, driverLocation, showRoute]);

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full rounded-2xl overflow-hidden cursor-crosshair" data-testid="map-container" />
      <div className="map-fade-bottom rounded-b-2xl pointer-events-none" />
    </div>
  );
};

export default MapComponent;
